import { supabase } from './supabase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

export interface BroadcastResponse {
  account: string;
  broadcast_date_id: string;
  created_at: string | null;
  modified_at: string | null;
  delivered: number;
  donations: number;
  amount_raised: number;
  client_cost: number;
  client_roi: number;
  client_roi_percentage: number;
  pricing_model: string;
  sb_cost?: number;
  model_type?: string;
  model_value?: number;
  hidden?: boolean;  // admin-only: row is hidden from clients
  uncapped_cost?: number;   // admin-only: cost before cost caps
  cost_cap_ids?: number[];  // admin-only: caps covering this send's day
}

export interface PricingInfo {
  model_type: string;
  model_value: number;
  label: string;
}

export interface UserDatasetsResponse {
  email: string;
  datasets: string[];
  is_admin: boolean;
  is_superadmin: boolean;
  pricing_info?: Record<string, PricingInfo>;
}

export async function fetchBroadcasts(params?: {
  account?: string;
  start_date?: string;
  end_date?: string;
}): Promise<BroadcastResponse[]> {
  const searchParams = new URLSearchParams();

  if (params?.account && params.account !== 'all') {
    searchParams.append('account', params.account);
  }
  if (params?.start_date) {
    searchParams.append('start_date', params.start_date);
  }
  if (params?.end_date) {
    searchParams.append('end_date', params.end_date);
  }

  const queryString = searchParams.toString();
  const endpoint = `/api/broadcasts${queryString ? `?${queryString}` : ''}`;

  return fetchWithAuth(endpoint);
}

export async function fetchUserDatasets(): Promise<UserDatasetsResponse> {
  return fetchWithAuth('/api/user/datasets');
}

export interface PricingModelOptionResponse {
  model_type: string;
  default_value: number;
  label: string;
}

export async function fetchPricingModels(): Promise<PricingModelOptionResponse[]> {
  return fetchWithAuth('/api/pricing-models');
}

export async function savePricingOverride(params: {
  account: string;
  broadcast_date_id: string;
  model_type: string;
  model_value: number;
}): Promise<{ success: boolean }> {
  return fetchWithAuth('/api/pricing-override', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function deletePricingOverride(params: {
  account: string;
  broadcast_date_id: string;
}): Promise<{ success: boolean }> {
  return fetchWithAuth('/api/pricing-override', {
    method: 'DELETE',
    body: JSON.stringify(params),
  });
}

// ---- Donor export (superadmin-only): download a broadcast-level donor CSV ----

export async function downloadDonorExport(account: string): Promise<void> {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  const res = await fetch(`${API_URL}/api/donor-export?account=${encodeURIComponent(account)}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Export failed' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${account}_donors.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ---- SMS donor audiences (superadmin-only): pooled AD1/AD2/AD3/AD1-ever ----
// The pull runs as a BigQuery job on the server; start it, poll status, then
// download each file once it's ready.

export type AudienceTier = 'ad1' | 'ad2' | 'ad3' | 'ever';

export interface AudienceStatus {
  state: 'none' | 'running' | 'ready' | 'failed';
  run_date: string;
  file_prefix: string;
  error?: string;
}

export async function getAudienceStatus(): Promise<AudienceStatus> {
  return fetchWithAuth('/api/sms-audiences/status');
}

export async function startAudiencePull(): Promise<AudienceStatus> {
  return fetchWithAuth('/api/sms-audiences/start', { method: 'POST' });
}

export async function downloadAudience(tier: AudienceTier, filename: string): Promise<void> {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  const res = await fetch(`${API_URL}/api/sms-audiences/download?tier=${tier}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Download failed' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ---- Accounts export (admin-only): every Switchboard org in the mirror ----

export async function downloadAccountsExport(): Promise<void> {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  const res = await fetch(`${API_URL}/api/accounts-export`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Export failed' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'switchboard_accounts.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ---- Hide/unhide day-rows from clients (admin-only) ----

export async function hideBroadcasts(params: {
  account: string;
  broadcast_date_ids: string[];
}): Promise<{ success: boolean }> {
  return fetchWithAuth('/api/hidden-broadcasts', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function unhideBroadcasts(params: {
  account: string;
  broadcast_date_ids: string[];
}): Promise<{ success: boolean }> {
  return fetchWithAuth('/api/hidden-broadcasts', {
    method: 'DELETE',
    body: JSON.stringify(params),
  });
}

// ---- Cost caps (admin-only): cap what an account pays over a date range ----

export interface CostCapResponse {
  id: number;
  account: string;
  start_date: string;
  end_date: string;
  cap_amount: number;
  used: number;
  left: number;
}

export interface CostCapInput {
  start_date: string;  // YYYY-MM-DD
  end_date: string;
  cap_amount: number;
}

export async function fetchCostCaps(account: string): Promise<CostCapResponse[]> {
  return fetchWithAuth(`/api/cost-caps?account=${encodeURIComponent(account)}`);
}

export async function createCostCap(params: CostCapInput & { account: string }): Promise<{ success: boolean }> {
  return fetchWithAuth('/api/cost-caps', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function updateCostCap(id: number, params: CostCapInput): Promise<{ success: boolean }> {
  return fetchWithAuth(`/api/cost-caps/${id}`, {
    method: 'PUT',
    body: JSON.stringify(params),
  });
}

export async function deleteCostCap(id: number): Promise<{ success: boolean }> {
  return fetchWithAuth(`/api/cost-caps/${id}`, { method: 'DELETE' });
}

export async function setAccountPricing(params: {
  account: string;
  model_type: string;
  model_value: number;
}): Promise<{ success: boolean }> {
  return fetchWithAuth('/api/account-pricing', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

// ---- New-account discovery (admin-only) ----

export interface UnmappedAccount {
  account: string;
  /** Account creation date, YYYY-MM-DD. Null if the account is absent from
   *  catalog.organizations (2 of 554 currently are). */
  created_date?: string | null;
  last_sent_date?: string | null;
  broadcast_count?: number;
}

export interface UnmappedAccountsResponse {
  unmapped_count: number;
  mirror_dataset_count: number;
  priced_account_count: number;
  accounts: UnmappedAccount[];
}

export async function fetchUnmappedAccounts(options?: {
  refresh?: boolean;
  activity?: boolean;
}): Promise<UnmappedAccountsResponse> {
  const params = new URLSearchParams();
  if (options?.refresh) params.set('refresh', '1');
  if (options?.activity) params.set('activity', '1');
  const qs = params.toString();
  return fetchWithAuth(`/api/unmapped-accounts${qs ? `?${qs}` : ''}`);
}

// ---- Google Sheets pricing import (admin-only, feature-flagged) ----

export interface SheetPricingDiffRow {
  account: string;
  date: string;
  current: { model_type: string; model_value: number };
  sheet: { model_type: string; model_value: number };
}

export interface SheetPricingSkippedRow {
  row_number?: number;
  account?: string;
  date_of_send?: string;
  reasons: string[];
}

export interface SheetPricingDiffResponse {
  diffs: SheetPricingDiffRow[];
  skipped: SheetPricingSkippedRow[];
}

export interface SheetPricingApplyRow {
  account: string;
  date: string;
  model_type: string;
  model_value: number;
}

export interface SheetPricingApplyResponse {
  applied: number;
  failed: Array<{ account?: string; date?: string; reason: string }>;
}

export async function fetchSheetPricingDiff(sheetUrl: string): Promise<SheetPricingDiffResponse> {
  return fetchWithAuth('/api/sheet-pricing/diff', {
    method: 'POST',
    body: JSON.stringify({ sheet_url: sheetUrl }),
  });
}

export async function applySheetPricing(rows: SheetPricingApplyRow[]): Promise<SheetPricingApplyResponse> {
  return fetchWithAuth('/api/sheet-pricing/apply', {
    method: 'POST',
    body: JSON.stringify({ rows }),
  });
}
